/**
 * @file uiActions.js
 * @location src/store/reducers/uiActions.js
 * @role 다크모드·UI 패치·온보딩 셋업·전체 데이터 초기화 액션 핸들러
 *
 *  TOGGLE_DARK       darkMode boolean 전환 → useEffect 에서 body.classList 반영
 *  SET_UI            { patch } — 모달 플래그 등 자유 패치 (전역 모달 열기/닫기)
 *  SET_UI_PREF       { key, value } — 단일 uiPrefs 키 덮어쓰기 (영속)
 *  SET_SECTION_OPEN  { uid, sectionKey, open } — 섹션 접힘 상태 (영속)
 *  SETUP_WORKSPACE   온보딩 완료 — 위저드 수집값으로 워크스페이스 시드
 *  RESET_DATA        tasks 초기화 + UI 상태 리셋 (설정의 '전체 데이터초기화')
 *  LOAD_STATE        { data } — JSON Import 영속 객체로 전 영속 필드 교체 (설정의 '복원')
 *
 *  ※ companyName 은 Supabase 팀명 — SETUP_WORKSPACE/HYDRATE 에서만 세팅 (직접 SET 액션 없음)
 */
import { INIT_MEMBERS } from '../../data/members.js';
import { newId } from '../../utils/ids.js';

export const uiActions = {
  TOGGLE_DARK: (state) => ({ ...state, darkMode: !state.darkMode }),

  SET_UI: (state, action) => ({ ...state, ...action.patch }),

  LOAD_STATE: (state, action) => ({ ...state, ...action.data }),

  // 온보딩 완료 — 데모 데이터를 비우고 위저드 수집값(매니저 본인)으로 시드.
  // RESET_DATA 와 달리 INIT_MEMBERS 가 아닌 입력 멤버로 시작 (빈 워크스페이스).
  // 첫 진입은 멤버 탭으로 착지 → 「멤버-초대」 안내 (razor ㉒ 단일 매니저).
  SETUP_WORKSPACE: (state, action) => {
    // 위저드 멤버는 id 가 없으므로 부여(기존 멤버 fallback 은 id 보유). email/mainCases 시드.
    const rawMembers = (action.members?.length
      ? action.members.map((m) => ({
          id: m.id ?? newId('member'),
          name: m.name,
          email: m.email ?? '',
          mainCases: Array.isArray(m.mainCases) ? m.mainCases : [],
          ...(m.isManager === true ? { isManager: true } : {}),
        }))
      : state.members.filter(m => m.id === state.uid));
    // razor v41: 매니저는 사람 단위 — 위저드 멤버에 isManager 표식 없으면 첫 멤버 자동 매니저
    // (워크스페이스 락 방지). 명시 매니저가 있으면 그대로 admin uid.
    const explicitAdminIdx = rawMembers.findIndex(m => m.isManager);
    const members = explicitAdminIdx >= 0
      ? rawMembers
      : rawMembers.map((m, i) => i === 0 ? { ...m, isManager: true } : m);
    const admin = explicitAdminIdx >= 0 ? rawMembers[explicitAdminIdx] : members[0];
    return {
      ...state,
      companyName:   action.companyName ?? state.companyName,
      members,
      uid:           admin.id,
      tasks:         [],
      deletedTasks:  [],
      onboarded:     true,
      activeNav:     'members',
    };
  },

  // ── UI 환경설정 영속화 (uiPrefs) ──

  /** 임의 uiPrefs 키를 단일 값으로 덮어쓴다. { key: string, value: any } */
  SET_UI_PREF: (state, action) => ({
    ...state,
    uiPrefs: {
      ...(state.uiPrefs ?? {}),
      [action.key]: action.value,
    },
  }),

  SET_SECTION_OPEN: (state, action) => ({
    ...state,
    uiPrefs: {
      ...(state.uiPrefs ?? { sectionOpen: {} }),
      sectionOpen: {
        ...(state.uiPrefs?.sectionOpen ?? {}),
        [action.uid]: {
          ...((state.uiPrefs?.sectionOpen ?? {})[action.uid] ?? {}),
          [action.sectionKey]: !!action.open,
        },
      },
    },
  }),

  RESET_DATA: (state) => ({
    ...state,
    members:       INIT_MEMBERS,
    uid:           INIT_MEMBERS[0].id,
    tasks:         [],
    deletedTasks:  [],
    uiPrefs:       { sectionOpen: {} },
    activeNav: 'tasks',
    progTargetId: null, taskModalAee: null,
    modalTask: false, modalProg: false,
    modalInviteMember: false, modalSettings: false,
  }),
};
